<template>
    <section class="page-auth-registration">
        <div class="page-auth-registration__header">
            <h1>Войти или зарегистрироваться</h1>
        </div>
        <div class="page-auth-registration__description" v-if="step === 'form'">
            Введите ваш номер телефона<br /> для получения кода авторизации
        </div>
        <div class="page-auth-registration__container">
            <template v-if="step === 'form'">
                <div class="page-auth-registration__form">
                    <ui-form
                        v-bind:model="fields"
                        v-bind:validation="$v"
                        v-bind:submit-handler="getAuthCode"
                        v-on:update="updateFormData"
                    />
                </div>
                <div class="page-auth-registration__recaptcha">
                    <vue-recaptcha
                        v-bind:sitekey="recaptchaKey"
                        v-on:verify="recaptchaVerifyHandler"
                        v-on:expired="recaptchaExpiredHandler"
                        load-recaptcha-script
                        ref="recaptcha"
                    />
                    <div class="page-auth-registration__recaptcha-error"
                         v-if="recaptchaError"
                         v-text="recaptchaError"
                    />
                </div>
                <div class="page-auth-registration__button">
                    <ui-button
                        v-on:click="getAuthCode"
                        v-bind:disabled="$v.$invalid && $v.$dirty"
                        size="lg"
                        text="Отправить"
                    />
                </div>
                <div class="page-auth-registration__agreement">
                    <span>
                        Нажимая «Отправить», вы соглашаетесь с условиями <a :href="urls.policies.userAgreement"
                                                                            target="_blank">Пользовательского соглашения</a>.
                        Мы обрабатываем ваши персональные данные на основании пункта&nbsp;5&nbsp;части&nbsp;1
                        статьи&nbsp;6 152&#8209;Ф3 «О персональных данных».
                        Больше информации в&nbsp;<a :href="urls.policies.privacyPolicy"
                                                    target="_blank">Политике конфиденциальности</a>.
                    </span>
                </div>
            </template>
            <template v-if="step === 'verification'">
                <div class="page-auth-registration__text _center">
                    Введите 6-значный код, отправленный на {{ fields.phone.value }}
                </div>
                <div class="page-auth-registration__verification">
                    <div class="page-auth-registration__verification-code">
                        <ui-input-character-set
                            v-bind:size="codeLength"
                            v-model="code"
                            v-on:submit="registration"
                            v-on:update="resetCodeError"
                            v-bind:is-invalid="isCodeInvalid"
                            is-autofocus
                        />
                    </div>
                    <div class="page-auth-registration__verification-help"
                         v-if="codeRequestTimer === 0"
                    >
                        <span class="page-auth-registration__verification-help-trigger" v-on:click="resetCode">
                            Отправить код повторно
                        </span>
                    </div>
                    <div class="page-auth-registration__verification-help"
                         v-else
                    >
                        <span v-text="'Повторно отправить код можно через '" /><span
                        class="page-auth-registration__verification-help-timer"
                        v-text="formattedTimerValue"
                    />
                    </div>
                </div>
                <div class="page-auth-registration__text _error"
                     v-if="unknownError"
                >
                    Не получилось обработать запрос. Попробуйте повторить
                </div>
                <div class="page-auth-registration__button">
                    <ui-button
                        v-on:click="registration"
                        v-bind:disabled="code.length !== codeLength || isCodeInvalid  || !isAgreementAccepted"
                        size="lg"
                        text="Подтвердить"
                    />
                </div>
            </template>
        </div>
    </section>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha';
import { mapState } from 'vuex';
import moment from 'moment';
import config from '~/config';
import forms from '~/forms';
import utils from '~/utils';

const localStorageReferralKey = config.localStorage.keyPrefix + config.localStorage.keyAffixies.referralCode;

export default {
    name: 'page-auth-login-registration',
    components: { VueRecaptcha },
    inject: [ 'image', 'recaptchaKey' ],
    props: {
        content: {
            type: String,
            default: 'login',
        },
    },
    data: () => ({
        step: 'form',
        recaptchaValue: null,
        urls: config.urls,
        codeLength: 6,
        code: '',
        isCodeInvalid: false,
        isReferralToBeDeleted: false,
        isAgreementAccepted: true,
        fields: {
            phone: {
                ...forms.fields.phone,
                placeholder: '+7',
                label: '',
            },
        },
    }),
    validations() {
        return {
            fields: {
                phone: { ...forms.validations.phone },
            },
            recaptchaValue: forms.validations.genericRequired.value,
            isAgreementAccepted: {
                isValid: value => value,
            },
        };
    },
    computed: {
        ...mapState('sms', [
            'getAuthCodeIsLoading',
            'getAuthCodeError',
            'getAuthCodeResult',
            'codeRequestTimer',
        ]),
        ...mapState('users', [
            'registrationResult',
            'registrationIsLoading',
            'registrationError',
        ]),
        recaptchaError() {
            if (this.$v.$invalid && this.$v.$dirty && !this.recaptchaValue) {
                return 'Подтвердите, что вы не робот';
            }
            return null;
        },
        isLoading() {
            return this.getAuthCodeIsLoading || this.registrationIsLoading;
        },
        formattedTimerValue() {
            return moment(this.codeRequestTimer).format('mm:ss');
        },
        agreementError() {
            if (this.$v.$invalid && this.$v.$dirty && !this.isAgreementAccepted) {
                return 'Необходимо ваше согласие';
            }
            return null;
        },
        unknownError() {
            return this.registrationError !== ' Invalid verification code' && !!this.registrationError;
        },
    },
    methods: {
        updateFormData(newData) {
            this.fields = newData;
        },
        recaptchaVerifyHandler(response) {
            this.recaptchaValue = response;
        },
        recaptchaExpiredHandler() {
            this.recaptchaValue = null;
        },
        resetRecaptcha() {
            if (this.$refs.recaptcha) {
                this.$refs.recaptcha.reset();
            }
        },
        resetCode() {
            this.code = '';
            this.step = 'form';
            this.$nextTick(this.resetRecaptcha);
        },
        getAuthCode() {
            if (this.$v.$invalid) {
                this.$v.$touch();
                return;
            }
            this.$store.dispatch('sms/getAuthCode', { value: this.fields.phone.value, recaptcha: this.recaptchaValue });
            this.step = 'verification';
        },
        registration() {
            if (this.code.length !== this.codeLength || this.isCodeInvalid) {
                return;
            }
            const params = {
                phone: this.fields.phone.value,
                code: this.code,
            };
            let referral = window.localStorage.getItem(localStorageReferralKey);
            if (referral && referral !== 'null') {
                params.referralCode = referral;
                this.isReferralToBeDeleted = true;
            }
            this.$store.dispatch('users/registration', params);
        },
        resetCodeError() {
            this.isCodeInvalid = false;
        },
        checkReferral() {
            const referralQuery = utils.common.parseUrl().find(x => x.title === config.common.referralQuery);
            let innerReferral = null;
            if (!referralQuery) {
                const params = new URLSearchParams(window.location.search);
                const next = params.get('next');
                const decodedNext = decodeURIComponent(next);
                const innerParams = new URLSearchParams(decodedNext.split('?')[1]);
                innerReferral = innerParams.get('referral');
            } // TODO fix redirect url /user/login/?next=%2Fuser%2Fregister%2F%3Freferral%3D7lgkgyxd6x4uwyey8gi3nzftk3nr5k
            if (referralQuery || innerReferral) {
                window.localStorage.setItem(localStorageReferralKey, referralQuery?.value || innerReferral);
            }
        },
        checkPhoneParam() {
            const phoneParam = new URLSearchParams(window.location.search).get('phone');
            if (phoneParam) {
                this.fields.phone.value = phoneParam;
            }
        },
    },
    mounted() {
        this.checkReferral();
        this.checkPhoneParam();
    },
    watch: {
        isLoading(newVal) {
            this.$parent.$emit('update-loading', newVal);
        },
        getAuthCodeIsLoading(newVal) {
            if (newVal) {
                this.$v.$reset();
                this.resetRecaptcha();
            }
        },
        registrationIsLoading(newVal, oldVal) {
            this.isCodeInvalid = this.registrationError === ' Invalid verification code';

            if (!newVal && oldVal && this.registrationResult) {
                if (this.isReferralToBeDeleted) {
                    window.localStorage.setItem(localStorageReferralKey, '');
                    this.isReferralToBeDeleted = false;
                }

                const next = new URLSearchParams(window.location.search).get('next');
                if (this.registrationResult.email) {
                    window.location.href = utils.common.getSafeRedirectUrl(next, this.urls.cabinet.dashboard);
                } else {
                    utils.analytics.ym.registerPhone();
                    window.location.href = this.urls.auth.registration + window.location.search;
                }
            }
        },
    },
};
</script>

<style scoped lang="less">
@import '~theme';

.page-auth-registration {
    flex: 1 1 auto;
    display: flex;
    align-items: stretch;
    justify-content: flex-start;
    flex-direction: column;
    gap: 32px;

    &__header {
        position: relative;

        flex: 0 0 auto;
        //padding-bottom: 8px;

        h1 {
            .typography-large();

            margin: 0;
            text-align: center;
        }
    }
    &__description {
        .typography-small();

        text-align: center;
    }
    &__container {
        flex: 1 1 auto;
        display: flex;
        align-items: stretch;
        justify-content: flex-start;
        flex-direction: column;
    }
    &__text {
        .typography-body();

        margin-bottom: 16px;
        &._center {
            text-align: center;
        }
        &._error {
            color: @color-accent-negative;
            text-align: center;
            & > a {
                color: @color-primary;
                text-decoration: underline;
            }
        }
    }
    &__success {
        text-align: center;
        &-title {
            .typography-large();

            margin-bottom: 16px;
            @media @media-md-down {
                margin-bottom: 32px;
            }
        }
        &-text {
            .typography-small();

            margin-bottom: 40px;
        }
    }
    &__form {
        flex: 0 0 auto;
    }
    &__agreement {
        .typography-body();

        &-error {
            .typography-body();

            margin-top: 16px;

            color: @color-accent-negative;
            text-align: center;
        }
    }
    &__recaptcha {
        flex: 0 0 auto;
        margin: 24px auto 24px;
        &-error {
            .typography-body();

            margin-top: 16px;

            color: @color-accent-negative;
            text-align: center;
        }
    }
    &__button {
        flex: 0 0 auto;
        min-width: 194px;
        margin: 0 auto 16px;
    }
    &__verification {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        width: 100%;
        margin-bottom: 40px;

        text-align: center;
        &-password {
            margin-bottom: 16px;
        }
        &-help {
            .typography-body();

            margin-top: 8px;

            text-align: center;
            &-trigger {
                cursor: pointer;

                color: @color-primary;
                text-decoration: underline;
                &:hover {
                    text-decoration: none;
                }
            }
            &-timer {
                display: inline-block;
                min-width: 40px;

                color: @color-primary;
                text-align: left;
            }
        }
    }
    &__login {
        .typography-body();

        flex: 0 0 auto;
        margin: auto 0 0;

        text-align: center;
        & a {
            color: @color-primary;
        }
    }

    @media @media-md-down {
        &__container {
            .container-paddings();

            padding-bottom: 32px;
        }
    }
    @media @media-xs-down {
        &__button {
            width: 100%;
        }
    }
}
</style>
